import type { SearchLocationMode } from "@/api/types";

interface RecentLocation {
  label: string;
  lat: number | null;
  lng: number | null;
  bounds: string | null;
  locationMode: SearchLocationMode | null;
}

const RECENT_LOCATIONS_KEY = "slotique_recent_locations";
const RECENT_LOCATIONS_MAX = 5;

function getRecentLocations(): RecentLocation[] {
  try {
    const raw = localStorage.getItem(RECENT_LOCATIONS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => {
        if (typeof item !== "object" || item == null) return false;
        const candidate = item as Partial<RecentLocation>;
        return typeof candidate.label === "string" && candidate.label.trim().length > 0;
      })
      .map((item) => {
        const candidate = item as Partial<RecentLocation>;
        const label = typeof candidate.label === "string" ? candidate.label.trim() : "";
        const lat = typeof candidate.lat === "number" ? candidate.lat : null;
        const lng = typeof candidate.lng === "number" ? candidate.lng : null;
        const bounds = typeof candidate.bounds === "string" ? candidate.bounds : null;
        return {
          label,
          lat,
          lng,
          bounds,
          locationMode: normalizeLocationMode(candidate.locationMode, bounds, lat, lng),
        };
      });
  } catch {
    return [];
  }
}

function saveRecentLocation(location: RecentLocation): void {
  const label = location.label.trim();
  if (!label) return;

  const normalized: RecentLocation = {
    label,
    lat: location.lat,
    lng: location.lng,
    bounds: location.bounds,
    locationMode: normalizeLocationMode(
      location.locationMode,
      location.bounds,
      location.lat,
      location.lng,
    ),
  };

  try {
    const existing = getRecentLocations();
    const key = toStorageKey(normalized);
    const filtered = existing.filter((entry) => toStorageKey(entry) !== key);
    const updated = [normalized, ...filtered].slice(0, RECENT_LOCATIONS_MAX);
    localStorage.setItem(RECENT_LOCATIONS_KEY, JSON.stringify(updated));
  } catch {
    // localStorage unavailable or quota exceeded
  }
}

function toStorageKey(location: RecentLocation): string {
  return `${location.label}|${location.lat ?? ""}|${location.lng ?? ""}|${location.bounds ?? ""}|${location.locationMode ?? ""}`;
}

function inferLocationMode(
  bounds: string | null,
  lat: number | null,
  lng: number | null,
): SearchLocationMode | null {
  if (bounds) return "place";
  if (lat != null && lng != null) return "coords";
  return null;
}

function normalizeLocationMode(
  value: unknown,
  bounds: string | null,
  lat: number | null,
  lng: number | null,
): SearchLocationMode | null {
  if (value === "place" || value === "viewport" || value === "coords") {
    return value;
  }
  return inferLocationMode(bounds, lat, lng);
}

export { getRecentLocations, type RecentLocation, saveRecentLocation };
